<template>
  <div>

      <form @submit.prevent="findPlayer">
        <div class="form-group">
          <label for="identityType">Identity type</label>
          <select v-model="idType" id="identityType" class="form-control">
            <option :value="SEARCH_TYPE_PLAYER_ID">Player id</option>
            <option :value="SEARCH_TYPE_NAME">Name</option>
            <option :value="SEARCH_TYPE_EXT_ID">Ext id</option>
            <option :value="SEARCH_TYPE_DEVICE_ID">Device id</option>
            <option :value="SEARCH_TYPE_FACEBOOK_ID">Facebook id</option>
            <option :value="SEARCH_TYPE_GOOGLE_ID">Google id</option>
            <option :value="SEARCH_TYPE_ORDER_ID">Order id</option>
            <option :value="SEARCH_TYPE_ADVERTISING_ID">Advertising id</option>
            <option :value="SEARCH_TYPE_NSTATS_ID">NStats id</option>
            <option :value="SEARCH_TYPE_DEVICE_TOKEN">Токен устройства</option>
          </select>
        </div>
        <div class="form-group">
          <label for="identityValue">Identity value</label>
          <input v-model="idValue" type="text" id="identityValue" class="form-control" v-focus>
        </div>
        <b-button type="submit" variant="primary">Find</b-button>
      </form>

      <div v-show="foundPlayers !== null" class="result">
        <h3>Search results</h3>

        <p v-show="resultMessage !== null">
          {{resultMessage}}
        </p>

        <table v-if="foundPlayers !== null && foundPlayers.length > 0" class="table text-nowrap">
          <thead class="thead-dark">
            <tr>
              <th scope="col">Id</th>
              <th scope="col">Name</th>
              <th scope="col">Level</th>
              <th scope="col">Last save time</th>
            </tr>
          </thead>
          <tr
            v-for="player in foundPlayers"
            :key="player.id"
          >
            <td>
              <router-link :to="{name: 'player_info', params: {playerId: player.id}}">
                {{player.id}}
              </router-link>
            </td>
            <td v-html="getHighlightedPlayerName(player)"></td>
            <td>
              {{player.level | getOptionalStr}}
            </td>
            <td>
              {{player.lastSaveTime | getOptionalMomentStr}}
            </td>
          </tr>
        </table>
      </div>

  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import { getPlayerName } from '../model/player.js';
import { getStringWithHighlightedSubstringCaseInsensitive } from '../util.js';

const SEARCH_TYPE_PLAYER_ID      =   0;
const SEARCH_TYPE_EXT_ID         =   1;
const SEARCH_TYPE_DEVICE_ID      =   2;
const SEARCH_TYPE_FACEBOOK_ID    =   3;
const SEARCH_TYPE_GOOGLE_ID      =   4;
const SEARCH_TYPE_ORDER_ID       = 100;
const SEARCH_TYPE_ADVERTISING_ID = 101;
const SEARCH_TYPE_NSTATS_ID      = 102;

const SEARCH_TYPE_NAME         = 1000;
const SEARCH_TYPE_DEVICE_TOKEN = 1001;

export default {
  name: 'PlayerFind',

  data() {
    return {
      SEARCH_TYPE_PLAYER_ID,
      SEARCH_TYPE_EXT_ID,
      SEARCH_TYPE_DEVICE_ID,
      SEARCH_TYPE_FACEBOOK_ID,
      SEARCH_TYPE_GOOGLE_ID,
      SEARCH_TYPE_ORDER_ID,
      SEARCH_TYPE_ADVERTISING_ID,
      SEARCH_TYPE_NSTATS_ID,
      SEARCH_TYPE_NAME,
      SEARCH_TYPE_DEVICE_TOKEN,

      idType: SEARCH_TYPE_PLAYER_ID,
      idValue: '',

      /** @type {Object[] | null} */
      foundPlayers: null,
    };
  },

  watch: {
    idType() {
      this.resetSearchResults();
    },

    idValue() {
      this.resetSearchResults();
    },
  },

  computed: {
    /** @returns {?string} */
    resultMessage() {
      if(this.foundPlayers !== null && this.foundPlayers.length === 0) {
        return this.getEmptyResultMessage;
      }
      return null;
    },

    /** @returns {string} */
    getEmptyResultMessage() {
        if(this.idType === SEARCH_TYPE_PLAYER_ID) {
          return 'Player not found.';
        } else {
          return 'Players not found.';
        }
    },
  },

  methods: {
    findPlayer() {
      notifier.deleteSenderNotifications(this);

      if(this.idType === SEARCH_TYPE_PLAYER_ID) {
        this.findPlayerById();
        return;
      }

      if(this.idType === SEARCH_TYPE_NAME) {
        this.findPlayersByName();
        return;
      }

      if(this.idType === SEARCH_TYPE_DEVICE_TOKEN) {
        this.findByDeviceToken();
        return;
      }

      const data = new FormData();
      data.set('id_type', this.idType.toString());
      data.set('id_val',  this.idValue.toString());

      HTTP.post('/player/find', data)
        .then(response => {
          const data = response.data.data;
          if(data === null) {
            notifier.showError('Invalid server response. data === null.', {sender: this});
            return;
          }

          const foundPlayers = data.slice();
          foundPlayers.sort((a, b) => a.id - b.id);
          this.foundPlayers = foundPlayers;
        })
        .catch(err => {
          notifier.showError(err, {sender: this});
        });
    },

    findPlayerById() {
      const playerIdStr = this.idValue.trim();
      const playerId = parseInt(playerIdStr, 10);
      if(isNaN(playerId)) {
        notifier.showError(
          'Player id must be an integer.',
          {sender: this}
        );
        return;
      }

      const url = `/player/${playerId}/is_known`;
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(!('isKnown' in data)) {
          notifier.showError(`Invalid server response. Property isKnown not found.`, {sender: this});
          return;
        }

        if(data.isKnown) {
          this.$router.push({name: 'player_info', params: {playerId: playerId}});
          return;
        }

        // Player with specified id does not exist.
        this.foundPlayers = [];
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    findPlayersByName() {
      const url = '/player/find_by_name';

      const requestConf = {
        params: {
          name_part: this.idValue,
        }
      };

      HTTP.get(url, requestConf)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.foundPlayers = data;
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    findByDeviceToken() {
      const url = '/player/find_by_device_token';

      const data = new FormData();
      data.set('device_token',  this.idValue);

      HTTP.post(url, data)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const foundPlayers = data.slice();
        foundPlayers.sort((a, b) => a.id - b.id);
        this.foundPlayers = foundPlayers;
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    resetSearchResults() {
      this.foundPlayers = null;
    },

    getHighlightedPlayerName(player) {
      const playerName = getPlayerName(player);
      if(playerName === null) {
        return null;
      }

      if(this.idType === SEARCH_TYPE_NAME) {
        return getStringWithHighlightedSubstringCaseInsensitive(playerName, this.idValue);
      }

      return playerName;
    },
    
  }
};
</script>

<style scoped>
.result {
  margin-top: 2rem;
}
</style>
